/**
 * True only when `url` is a string that resolves to an http(s) URL safe to open.
 *
 * Guards navigation / `window.open` — defense in depth for backend/CMS-supplied links such as
 * an alert's `ctaUrl`, even when the BFF is trusted — against:
 * - non-string input: a malformed payload (e.g. an array `ctaUrl`) must be rejected here, not
 *   throw on `.trim()` and blank the portal-wide header;
 * - non-web schemes (`javascript:`, `data:`, `vbscript:`, …);
 * - off-origin open redirects: `//host` and `/\host` inputs that `new URL` would otherwise
 *   resolve to another origin.
 *
 * Absolute `http(s)://` URLs may point anywhere (the CMS owns them); non-absolute inputs are
 * allowed only when they resolve to the current origin. Unparseable input is rejected.
 */
export const isSafeHttpUrl = (url: unknown): boolean => {
  if (typeof url !== 'string') {
    return false;
  }
  const trimmed = url.trim();
  if (trimmed.length === 0) {
    // Blank / whitespace resolves to the origin root — not a real CTA target.
    return false;
  }
  try {
    const parsed = new URL(trimmed, window.location.origin);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return false;
    }
    // A non-absolute input (relative path, `//host`, `/\host`) must stay on our origin —
    // otherwise `new URL` silently resolves it off-site into an open redirect.
    const isAbsolute = /^https?:\/\//i.test(trimmed);
    return isAbsolute || parsed.origin === window.location.origin;
  } catch {
    return false;
  }
};
